import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
/**
 * Convert HTML to formatted plain text for PDF, preserving structure
 * @param {string} html - HTML content from rich text editor
 * @returns {string} - Formatted plain text with line breaks preserved
 */
const stripHtmlTags = (html) => {
  if (!html) return '';

  let text = html;

  // Convert block elements to line breaks
  text = text.replace(/<\/p>/gi, '\n');
  text = text.replace(/<p[^>]*>/gi, '');
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/div>/gi, '\n');
  text = text.replace(/<div[^>]*>/gi, '');

  // Convert list items to bullet points
  text = text.replace(/<li[^>]*>/gi, '• ');
  text = text.replace(/<\/li>/gi, '\n');

  // Remove list container tags
  text = text.replace(/<\/?ul[^>]*>/gi, '');
  text = text.replace(/<\/?ol[^>]*>/gi, '');

  // Remove all remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');

  // Decode HTML entities
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up multiple consecutive line breaks (max 2)
  text = text.replace(/\n{3,}/g, '\n\n');

  // Clean up spaces on each line but preserve line breaks
  text = text.split('\n').map(line => line.replace(/\s+/g, ' ').trim()).join('\n');

  // Trim leading/trailing whitespace
  text = text.trim();

  return text;
};

/**
 * Generate PDF from Progress Summary data
 * @param {Object} progressSummary - The progress summary data
 * @returns {jsPDF} - The generated PDF document
 */
export const generateProgressSummaryPDF = async (progressSummary) => {
  console.log(`[Progress Summary PDF] Generating PDF for:`, progressSummary);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Load and add logo
  let yPosition = 15;
  try {
    // Create a promise to load the logo image
    const logoPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = '/listenhear_logo.png';
    });

    const logoImg = await logoPromise;
    const logoWidth = 80;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth; // Maintain aspect ratio
    const logoX = (pageWidth - logoWidth) / 2;
    doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
    yPosition += logoHeight + 8;
  } catch (error) {
    console.warn('[Progress Summary PDF] Could not load logo:', error);
    yPosition = 20; // Start without logo if it fails
  }

  // Helper function to add text with word wrap
  // Line height factor: jsPDF uses ~1.15 line spacing, and 1 point ≈ 0.352778mm
  // Combined factor: 0.352778 * 1.15 ≈ 0.406, but we use actual getLineHeight for accuracy
  const getLineHeight = (fontSize) => {
    // More accurate: fontSize in points * 0.352778 mm/pt * 1.15 line spacing
    return fontSize * 0.352778 * 1.15;
  };

  const addText = (text, x, y, options = {}) => {
    const {
      fontSize = 10,
      fontStyle = 'normal',
      align = 'left',
      maxWidth = pageWidth - 40,
    } = options;

    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);

    const lineHeight = getLineHeight(fontSize);

    // Split by explicit line breaks first, then word-wrap each paragraph
    const paragraphs = text.split('\n');
    let allLines = [];

    paragraphs.forEach((paragraph) => {
      if (paragraph.trim() === '') {
        // Preserve empty lines as spacing (half height)
        allLines.push('');
      } else {
        const wrappedLines = doc.splitTextToSize(paragraph, maxWidth);
        allLines = allLines.concat(wrappedLines);
      }
    });

    if (align === 'center') {
      doc.text(allLines, pageWidth / 2, y, { align: 'center' });
    } else {
      doc.text(allLines, x, y);
    }

    // Calculate height: full height for text lines, half for empty lines
    let totalHeight = 0;
    allLines.forEach((line) => {
      totalHeight += line === '' ? lineHeight * 0.5 : lineHeight;
    });

    return totalHeight;
  };

  // Header
  addText('Speech & Language Progress Summary Report', pageWidth / 2, yPosition, {
    fontSize: 16,
    fontStyle: 'bold',
    align: 'center',
  });
  yPosition += 6;

  // Use clinic address if available, otherwise use default Hearsay address
  let clinicAddress = '44 Main St. E., Milton, ON L9T 1N3 • Tel: (905) 875-3345 • Fax: (905) 875-0343';
  let clinicEmail = 'milton@hearsay.ca';

  if (progressSummary.clinicId) {
    const clinic = progressSummary.clinicId;
    console.log(`[Progress Summary PDF] Using clinic data:`, clinic);

    if (clinic.address || clinic.phoneNumber) {
      const addressParts = [];
      if (clinic.address) addressParts.push(clinic.address);
      if (clinic.phoneNumber) addressParts.push(`Tel: ${clinic.phoneNumber}`);
      clinicAddress = addressParts.join(' • ');
    }

    if (clinic.email) {
      clinicEmail = clinic.email;
    }
  }

  addText(clinicAddress, pageWidth / 2, yPosition, {
    fontSize: 9,
    align: 'center',
  });
  yPosition += 4;

  addText(`e-mail: ${clinicEmail}`, pageWidth / 2, yPosition, {
    fontSize: 9,
    align: 'center',
  });
  yPosition += 6;

  // Add a line separator
  doc.setLineWidth(0.5);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 5;

  // Basic Information Section
  const formatDate = (date) => {
    if (!date) return 'N/A';
    // Parse date string and use UTC to avoid timezone offset issues
    const dateStr = typeof date === 'string' ? date.split('T')[0] : date.toISOString().split('T')[0];
    const [year, month, day] = dateStr.split('-');
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  };

  // Client Information
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Client Information', 20, yPosition);
  yPosition += 6;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const leftColumn = 20;
  const rightColumn = 110;
  const lineHeight = 6;

  // Left column
  doc.text(`Client Name: ${progressSummary.clientName || 'N/A'}`, leftColumn, yPosition);
  doc.text(`Date: ${formatDate(progressSummary.date)}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`D.O.B.: ${formatDate(progressSummary.dateOfBirth)}`, leftColumn, yPosition);
  doc.text(`C.A.: ${progressSummary.chronologicalAge || 'N/A'}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Grade: ${progressSummary.grade || 'N/A'}`, leftColumn, yPosition);
  doc.text(`School: ${progressSummary.school || 'N/A'}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Supervising SLP: ${progressSummary.supervisingSLP || 'N/A'}`, leftColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Clinician(s): ${progressSummary.clinicians || 'N/A'}`, leftColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Session #: ${progressSummary.sessionNumber || 'N/A'}`, leftColumn, yPosition);
  yPosition += lineHeight + 2;

  // Current Communication Status
  if (progressSummary.currentCommunicationStatus) {
    doc.setFont('helvetica', 'bold');
    doc.text('Current Communication Status:', leftColumn, yPosition);
    yPosition += 4;

    doc.setFont('helvetica', 'normal');
    const statusText = stripHtmlTags(progressSummary.currentCommunicationStatus);
    const statusHeight = addText(
      statusText,
      leftColumn,
      yPosition,
      { maxWidth: pageWidth - 40 }
    );
    yPosition += statusHeight + 3;
  }

  // Treatment Plan
  if (progressSummary.treatmentPlan) {
    doc.setFont('helvetica', 'bold');
    doc.text('Treatment Plan:', leftColumn, yPosition);
    yPosition += 4;

    doc.setFont('helvetica', 'normal');
    const planText = stripHtmlTags(progressSummary.treatmentPlan);
    const planHeight = addText(
      planText,
      leftColumn,
      yPosition,
      { maxWidth: pageWidth - 40 }
    );
    yPosition += planHeight + 3;
  }

  // Check if we need a new page before the table
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  // Goals and Progress Table
  doc.setFont('helvetica', 'bold');
  doc.text('Goals and Progress', leftColumn, yPosition);
  yPosition += 4;

  if (progressSummary.goals && progressSummary.goals.length > 0) {
    const tableData = progressSummary.goals.map((goal) => {
      // Use X for checked or empty string for unchecked to show selected status
      const notYetTargeted = goal.notYetTargeted ? 'X' : '';
      const emergingMaximum = goal.emergingMaximum ? 'X' : '';
      const emergingModerate = goal.emergingModerate ? 'X' : '';
      const emergingMinimal = goal.emergingMinimal ? 'X' : '';
      const achieved = goal.achieved ? 'X' : '';

      return [
        stripHtmlTags(goal.description) || '',
        notYetTargeted,
        emergingMaximum,
        emergingModerate,
        emergingMinimal,
        achieved,
        stripHtmlTags(goal.actions) || ''
      ];
    });

    autoTable(doc, {
      startY: yPosition,
      head: [[
        'Goal Description',
        'Not Yet Targeted',
        'Emerging with Maximum Support',
        'Emerging with Moderate Support',
        'Emerging with Minimal Support',
        'Achieved',
        'Actions'
      ]],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [66, 66, 66],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'center',
        cellPadding: 1.5,
      },
      columnStyles: {
        0: { cellWidth: 50, valign: 'top' }, // Goal Description
        1: { cellWidth: 14, halign: 'center', valign: 'middle', fontSize: 9 }, // Not Yet Targeted
        2: { cellWidth: 14, halign: 'center', valign: 'middle', fontSize: 9 }, // Emerging Maximum
        3: { cellWidth: 14, halign: 'center', valign: 'middle', fontSize: 9 }, // Emerging Moderate
        4: { cellWidth: 14, halign: 'center', valign: 'middle', fontSize: 9 }, // Emerging Minimal
        5: { cellWidth: 14, halign: 'center', valign: 'middle', fontSize: 9 }, // Achieved
        6: { cellWidth: 50, valign: 'top' }, // Actions
      },
      margin: { left: 20, right: 20 },
    });

    yPosition = doc.lastAutoTable.finalY + 4;
  }

  // Check if we need a new page
  if (yPosition > 240) {
    doc.addPage();
    yPosition = 20;
  }

  // Comments
  if (progressSummary.comments) {
    doc.setFont('helvetica', 'bold');
    doc.text('Comments:', leftColumn, yPosition);
    yPosition += 4;

    doc.setFont('helvetica', 'normal');
    const commentsText = stripHtmlTags(progressSummary.comments);
    const commentsHeight = addText(
      commentsText,
      leftColumn,
      yPosition,
      { maxWidth: pageWidth - 40 }
    );
    yPosition += commentsHeight + 3;
  }

  // Check if we need a new page
  if (yPosition > 240) {
    doc.addPage();
    yPosition = 20;
  }

  // Next Steps
  if (progressSummary.nextSteps) {
    doc.setFont('helvetica', 'bold');
    doc.text('Next Steps:', leftColumn, yPosition);
    yPosition += 4;

    doc.setFont('helvetica', 'normal');
    const stepsText = stripHtmlTags(progressSummary.nextSteps);
    const stepsHeight = addText(
      stepsText,
      leftColumn,
      yPosition,
      { maxWidth: pageWidth - 40 }
    );
    yPosition += stepsHeight + 4;
  }

  // Footer - Signature Section
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  yPosition += 3;
  doc.setLineWidth(0.3);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 4;

  // Display supervising SLP if provided
  if (progressSummary.supervisingSLP) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    addText(progressSummary.supervisingSLP, pageWidth / 2, yPosition, {
      fontSize: 9,
      fontStyle: 'bold',
      align: 'center',
    });
    yPosition += 4;

    // Determine qualifications to display
    let qualifications = progressSummary.supervisingSLPQualifications;

    // If qualifications not stored in progress summary, look up from populated user data
    if (!qualifications && progressSummary.supervisingSLPId) {
      qualifications = progressSummary.supervisingSLPId.invoiceDescriptor;
      console.log(`[Progress Summary PDF] Looking up qualifications from user: ${qualifications}`);
    }

    // Display qualifications if found
    if (qualifications) {
      doc.setFont('helvetica', 'normal');
      addText(qualifications, pageWidth / 2, yPosition, {
        fontSize: 9,
        align: 'center',
      });
    }
  }

  return doc;
};

/**
 * Download Progress Summary as PDF
 * @param {Object} progressSummary - The progress summary data
 * @param {string} filename - Optional custom filename
 */
export const downloadProgressSummaryPDF = async (progressSummary, filename) => {
  const doc = await generateProgressSummaryPDF(progressSummary);
  const defaultFilename = filename || `progress-summary-${progressSummary.clientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(defaultFilename);
  console.log(`[Progress Summary PDF] Downloaded: ${defaultFilename}`);
};
